import React, {
  ChangeEvent,
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  LinearProgress,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';

import { env } from 'env';
import { useStyles } from './styles';
import {
  useReportIt,
  ReportFormValues,
} from '@services/reportIt/useReportIt';
import {
  ReportIncidentType,
  ReportAttachmentFile,
  ReportAttachmentPolicy,
  ReportAttachmentSource,
  ReportSymptomHint,
  getReportPolicy,
} from '@services/reportIt/reportItApi';
import { normalizeScreenshotFile } from '@services/reportIt/imageNormalize';
import { useReportItContext } from './useReportItContext';

const MAX_SCREENSHOT_BYTES = 10 * 1024 * 1024;
const MAX_SCREENSHOTS = 3;

const INCIDENT_OPTIONS: Array<{ value: ReportIncidentType; labelKey: string }> = [
  { value: 'black_screen', labelKey: 'reportIssue.incidentTypes.blackScreen' },
  { value: 'audio_only', labelKey: 'reportIssue.incidentTypes.audioOnly' },
  { value: 'frozen_video', labelKey: 'reportIssue.incidentTypes.frozenVideo' },
  { value: 'disconnect', labelKey: 'reportIssue.incidentTypes.disconnected' },
  { value: 'other', labelKey: 'reportIssue.incidentTypes.other' },
];

const SYMPTOM_OPTIONS: Array<{ value: ReportSymptomHint; labelKey: string }> = [
  { value: 'screen_black', labelKey: 'reportIssue.symptoms.screenBlack' },
  {
    value: 'audio_still_playing',
    labelKey: 'reportIssue.symptoms.audioStillPlays',
  },
  { value: 'video_frozen', labelKey: 'reportIssue.symptoms.videoFrozen' },
  { value: 'error_popup', labelKey: 'reportIssue.symptoms.errorMessage' },
  {
    value: 'controls_not_working',
    labelKey: 'reportIssue.symptoms.controlsNotWorking',
  },
  { value: 'disconnected', labelKey: 'reportIssue.symptoms.disconnected' },
  { value: 'other', labelKey: 'reportIssue.symptoms.other' },
];

const DEFAULT_HINTS_BY_INCIDENT: Partial<
  Record<ReportIncidentType, ReportSymptomHint[]>
> = {
  black_screen: ['screen_black', 'audio_still_playing'],
  audio_only: ['audio_still_playing', 'screen_black'],
  frozen_video: ['video_frozen'],
  disconnect: ['disconnected'],
};

// Opt-in feature flag for the Report It entry in the profile dropdown — matches
// the repo convention (every other REACT_APP_FEATURE_* gate is `=== 'true'`).
// Hidden unless the env explicitly enables it, so it can be shown/hidden per env.
export const reportIssueFeatureEnabled = (): boolean =>
  env.REACT_APP_FEATURE_REPORT_IT === 'true';

const defaultHints = (incidentType: ReportIncidentType): ReportSymptomHint[] =>
  DEFAULT_HINTS_BY_INCIDENT[incidentType] ?? [];

const cleanDescription = (value: string): string | undefined => {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const fileToAttachment = (
  file: File,
  source: ReportAttachmentSource,
): ReportAttachmentFile => ({
  file,
  filename: file.name,
  contentType: file.type,
  byteSize: file.size,
  source,
});

const imageFilesFrom = (list: FileList | null | undefined): File[] =>
  Array.from(list ?? []).filter(
    (file) => file.type === '' || file.type.startsWith('image/'),
  );

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) {
    return `${bytes} B`;
  }

  const kb = bytes / 1024;

  if (kb < 1024) {
    return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`;
  }

  const mb = kb / 1024;

  return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`;
};

// Object-URL previews are best-effort: jsdom (tests) and locked-down embeds may
// not expose createObjectURL, in which case we simply skip the thumbnail.
const safeObjectUrl = (file: File): string | undefined => {
  if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
    return undefined;
  }

  try {
    return URL.createObjectURL(file);
  } catch {
    return undefined;
  }
};

export interface ReportItDialogProps {
  open: boolean;
  onClose: () => void;
}

const ReportItDialog = ({ open, onClose }: ReportItDialogProps) => {
  const classes = useStyles();
  const { t: translate } = useTranslation('common');
  const { context, hasLiveExperience, failureReason } = useReportItContext();

  const [incidentType, setIncidentType] =
    useState<ReportIncidentType>('black_screen');
  const [description, setDescription] = useState('');
  const [symptomHints, setSymptomHints] = useState<ReportSymptomHint[]>(
    defaultHints('black_screen'),
  );
  const [attachments, setAttachments] = useState<ReportAttachmentFile[]>([]);
  const [attachmentPolicy, setAttachmentPolicy] =
    useState<ReportAttachmentPolicy | null>(null);
  const [attachmentError, setAttachmentError] = useState<string | undefined>();
  const [isProcessingAttachment, setIsProcessingAttachment] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const closeActionRef = useRef<HTMLButtonElement | null>(null);
  const prevOpenRef = useRef(false);

  const reportIt = useReportIt(context);
  const isSubmitting = reportIt.status === 'submitting';
  const isSuccess = reportIt.status === 'success';

  const resetForm = useCallback(() => {
    setIncidentType('black_screen');
    setDescription('');
    setSymptomHints(defaultHints('black_screen'));
    setAttachments([]);
    setAttachmentError(undefined);
    reportIt.reset();
  }, [reportIt]);

  // Reset form (and optionally prefill for event-screen failure reports) only
  // on the false → true transition so that mid-session state changes (e.g.
  // upload-progress rerenders) do not wipe the form while it is open.
  useEffect(() => {
    const didOpen = open && !prevOpenRef.current;
    prevOpenRef.current = open;

    if (!didOpen) {
      return;
    }

    resetForm();

    if (!hasLiveExperience && failureReason) {
      setIncidentType('disconnect');
      setSymptomHints(defaultHints('disconnect'));
      // failureReason is the i18n key of the notification shown when the
      // experience failed — its translation is already a complete, localized
      // sentence, so it makes a good editable report description. translate()
      // returns the input unchanged if it isn't a known key, so this is safe.
      setDescription(translate(failureReason));
    }
  }, [open, resetForm, hasLiveExperience, failureReason, translate]);

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      onClose();
    }
  }, [isSubmitting, onClose]);

  const handleIncidentChange = useCallback(
    (value: ReportIncidentType) => {
      setIncidentType(value);
      setSymptomHints(defaultHints(value));
      reportIt.reset();
    },
    [reportIt],
  );

  const handleSymptomToggle = useCallback((value: ReportSymptomHint) => {
    setSymptomHints((prev) =>
      prev.includes(value)
        ? prev.filter((item) => item !== value)
        : [...prev, value],
    );
  }, []);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    let cancelled = false;

    getReportPolicy()
      .then((policy) => {
        if (!cancelled) {
          setAttachmentPolicy(policy.attachmentPolicy);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAttachmentPolicy(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open]);

  const attachmentsEnabled = !!attachmentPolicy?.attachmentsEnabled;

  // Thumbnail previews — one object URL per attachment, revoked when the set
  // changes or the dialog unmounts so we don't leak blob URLs.
  const previews = useMemo(
    () => attachments.map((attachment) => safeObjectUrl(attachment.file)),
    [attachments],
  );

  useEffect(
    () => () => {
      previews.forEach((url) => {
        if (url) {
          URL.revokeObjectURL(url);
        }
      });
    },
    [previews],
  );

  // On success, move focus to the close action so keyboard / screen-reader
  // users land on the actionable control instead of a now-removed form field.
  useEffect(() => {
    if (isSuccess) {
      closeActionRef.current?.focus();
    }
  }, [isSuccess]);

  const addFiles = useCallback(
    async (files: File[], source: ReportAttachmentSource): Promise<void> => {
      setAttachmentError(undefined);

      if (files.length === 0) {
        return;
      }

      // Normalisation can take a moment (HEIC transcode), so flag a processing
      // state that drives the attach-button spinner and blocks submit until the
      // files have actually landed in the list.
      setIsProcessingAttachment(true);

      try {
        const next = [...attachments];

        for (const file of files) {
          if (next.length >= MAX_SCREENSHOTS) {
            setAttachmentError(
              translate('reportIssue.errors.maxScreenshots', {
                count: MAX_SCREENSHOTS,
              }),
            );
            break;
          }

          // Normalise to a web-safe image (PNG/JPEG/WebP). Mobile pickers — iOS
          // especially — hand back HEIC or a blank MIME type; these are transcoded
          // to JPEG so they pass the backend allowlist and render for operators.
          // eslint-disable-next-line no-await-in-loop
          const normalized = await normalizeScreenshotFile(file);

          if (!normalized.file) {
            setAttachmentError(translate('reportIssue.errors.invalidScreenshotType'));
            continue;
          }

          if (normalized.file.size > MAX_SCREENSHOT_BYTES) {
            setAttachmentError(translate('reportIssue.errors.screenshotTooLarge'));
            continue;
          }

          next.push(fileToAttachment(normalized.file, source));
        }

        setAttachments(next);
      } finally {
        setIsProcessingAttachment(false);
      }
    },
    [attachments, translate],
  );

  const handleAttachmentSelect = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const selected = Array.from(event.target.files ?? []);
      event.target.value = '';
      void addFiles(selected, 'manual_upload');
    },
    [addFiles],
  );

  const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  }, []);

  const handleDragEnter = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();

      if (!isSubmitting) {
        setIsDragging(true);
      }
    },
    [isSubmitting],
  );

  const handleDragLeave = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      // Ignore leave events fired when moving onto a child element.
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
        setIsDragging(false);
      }
    },
    [],
  );

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDragging(false);

      if (isSubmitting) {
        return;
      }

      void addFiles(imageFilesFrom(event.dataTransfer?.files), 'drag_drop');
    },
    [addFiles, isSubmitting],
  );

  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLDivElement>) => {
      if (!attachmentsEnabled || isSubmitting) {
        return;
      }

      const pasted = Array.from(event.clipboardData?.items ?? [])
        .filter((item) => item.kind === 'file' && item.type.startsWith('image/'))
        .map((item) => item.getAsFile())
        .filter((file): file is File => !!file);

      if (pasted.length === 0) {
        // Not an image paste — let normal text paste proceed (e.g. description).
        return;
      }

      event.preventDefault();
      void addFiles(pasted, 'paste');
    },
    [addFiles, attachmentsEnabled, isSubmitting],
  );

  const handleRemoveAttachment = useCallback((index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
    setAttachmentError(undefined);
  }, []);

  const handleSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();

      const values: ReportFormValues = {
        incidentType,
        description: cleanDescription(description),
        symptomHints,
        severityHint:
          incidentType === 'black_screen' || incidentType === 'disconnect'
            ? 'high'
            : undefined,
        attachDiagnostics: true,
        attachments: attachmentsEnabled ? attachments : undefined,
      };

      await reportIt.submit(values);
    },
    [incidentType, description, symptomHints, attachmentsEnabled, attachments, reportIt],
  );

  const stopGameInputPropagation = useCallback((event: React.SyntheticEvent) => {
    event.stopPropagation();
  }, []);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: classes.dialogPaper,
      }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
        onKeyDown={stopGameInputPropagation}
        onKeyUp={stopGameInputPropagation}
        onPointerDown={stopGameInputPropagation}
        onPointerUp={stopGameInputPropagation}
        onMouseDown={stopGameInputPropagation}
        onMouseUp={stopGameInputPropagation}
        onPaste={handlePaste}
      >
        <DialogTitle
          sx={classes.title}
        >
          {translate('reportIssue.title')}
          <IconButton
            aria-label={translate('reportIssue.close')}
            onClick={handleClose}
            disabled={isSubmitting}
            size="small"
            sx={classes.closeButton}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={classes.dialogContent}>
          {isSuccess ? (
            <Box sx={classes.successContent}>
              <Alert severity="success" sx={classes.successAlert}>
                {translate('reportIssue.success', {
                  reportId: reportIt.result?.reportId,
                })}
              </Alert>
              {reportIt.attachmentUploadFailed ? (
                <Alert severity="warning" sx={classes.attachmentWarning}>
                  {translate('reportIssue.attachmentUploadWarning')}
                </Alert>
              ) : null}
              <Typography variant="body2" sx={classes.successDetail}>
                {translate('reportIssue.successDetail')}
              </Typography>
            </Box>
          ) : (
            <>
              {reportIt.status === 'error' ? (
                <Alert severity="error">
                  {reportIt.error || translate('reportIssue.errors.submitFailed')}
                </Alert>
              ) : null}

              <TextField
                select
                label={translate('reportIssue.issueType')}
                value={incidentType}
                onChange={(event) =>
                  handleIncidentChange(event.target.value as ReportIncidentType)
                }
                disabled={isSubmitting}
                fullWidth
                sx={classes.field}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      sx: classes.menuPaper,
                    },
                  },
                }}
                inputProps={{ 'data-testid': 'report-issue-type' }}
              >
                {INCIDENT_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {translate(option.labelKey)}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label={translate('reportIssue.descriptionLabel')}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                disabled={isSubmitting}
                fullWidth
                multiline
                minRows={4}
                autoFocus
                sx={classes.field}
                inputProps={{ maxLength: 2000 }}
                helperText={
                  description.length > 0 ? `${description.length} / 2000` : undefined
                }
                FormHelperTextProps={{ sx: classes.charCounter }}
              />

              <FormControl component="fieldset" disabled={isSubmitting}>
                <Typography
                  component="legend"
                  sx={classes.legend}
                >
                  {translate('reportIssue.symptomsLabel')}
                </Typography>
                <FormGroup
                  sx={classes.symptomGrid}
                >
                  {SYMPTOM_OPTIONS.map((option) => (
                    <FormControlLabel
                      key={option.value}
                      control={
                        <Checkbox
                          checked={symptomHints.includes(option.value)}
                          onChange={() => handleSymptomToggle(option.value)}
                          sx={classes.checkbox}
                        />
                      }
                      sx={classes.checkboxLabel}
                      label={translate(option.labelKey)}
                    />
                  ))}
                </FormGroup>
              </FormControl>

              {attachmentsEnabled ? (
                <Box
                  sx={isDragging ? classes.dropZoneActive : classes.dropZone}
                  onDragEnter={handleDragEnter}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  data-testid="report-issue-dropzone"
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    hidden
                    onChange={handleAttachmentSelect}
                    data-testid="report-issue-attachments-input"
                  />
                  <Button
                    type="button"
                    startIcon={
                      isProcessingAttachment ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <AttachFileIcon />
                      )
                    }
                    onClick={() => fileInputRef.current?.click()}
                    disabled={
                      isSubmitting ||
                      isProcessingAttachment ||
                      attachments.length >= MAX_SCREENSHOTS
                    }
                    variant="outlined"
                    sx={classes.attachButton}
                  >
                    {isProcessingAttachment
                      ? translate('reportIssue.processing')
                      : translate('reportIssue.attachScreenshot')}
                  </Button>
                  <Typography variant="caption" sx={classes.attachHint}>
                    {isDragging
                      ? translate('reportIssue.dropActive')
                      : translate('reportIssue.attachHint')}
                  </Typography>

                  {attachmentError ? (
                    <Alert severity="warning" sx={classes.attachmentWarning}>
                      {attachmentError}
                    </Alert>
                  ) : null}

                  {attachments.length > 0 ? (
                    <Box sx={classes.attachmentList}>
                      {attachments.map((attachment, index) => (
                        <Box
                          key={`${attachment.filename}-${index}`}
                          sx={classes.attachmentItem}
                        >
                          {previews[index] ? (
                            <Box
                              component="img"
                              src={previews[index]}
                              alt={
                                attachment.filename ||
                                translate('reportIssue.screenshotFallbackName')
                              }
                              sx={classes.attachmentThumbnail}
                            />
                          ) : null}
                          <Box sx={classes.attachmentInfo}>
                            <Typography variant="body2" noWrap>
                              {attachment.filename ||
                                translate('reportIssue.screenshotIndexedName', {
                                  index: index + 1,
                                })}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={classes.attachmentMeta}
                            >
                              {formatBytes(attachment.byteSize)}
                            </Typography>
                            {reportIt.phase === 'uploading' ? (
                              <LinearProgress
                                variant="determinate"
                                value={Math.round(
                                  (reportIt.uploadProgress[index] ?? 0) * 100,
                                )}
                                sx={classes.uploadProgress}
                              />
                            ) : null}
                          </Box>
                          <IconButton
                            aria-label={translate('reportIssue.removeAttachment', {
                              filename:
                                attachment.filename ||
                                translate('reportIssue.screenshotFallbackName'),
                            })}
                            size="small"
                            onClick={() => handleRemoveAttachment(index)}
                            disabled={isSubmitting}
                            sx={classes.iconButton}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  ) : null}
                </Box>
              ) : null}
            </>
          )}
        </DialogContent>

        <DialogActions sx={classes.actions}>
          <Button
            ref={closeActionRef}
            onClick={handleClose}
            disabled={isSubmitting}
            data-testid="report-issue-close-action"
            sx={classes.cancelButton}
          >
            {isSuccess
              ? translate('reportIssue.close')
              : translate('reportIssue.cancel')}
          </Button>
          {!isSuccess ? (
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting || isProcessingAttachment}
              startIcon={
                isSubmitting ? (
                  <CircularProgress size={18} color="inherit" />
                ) : undefined
              }
              data-testid="report-issue-submit"
              sx={classes.submitButton}
            >
              {isSubmitting
                ? translate(
                    reportIt.phase === 'uploading'
                      ? 'reportIssue.uploading'
                      : 'reportIssue.sending',
                  )
                : translate('reportIssue.submit')}
            </Button>
          ) : null}
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default ReportItDialog;
